import { AlertType } from '@aloft/toolkit-shared';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PropertyGridCellKey } from '../../components/PropertyGrid/types.ts';
import { Tkv2ReferenceBarProperty } from '../../controller/types.ts';
import { ReferenceViewID, formatReferenceViewID } from '../../modules/referenceView/index.ts';
import { DataSheet } from '../../modules/referenceView/types.ts';
import { ReferenceBarUIState } from '../types.ts';
import { clearOrder } from './actions/clearOrder.ts';
import { processAlertsForRoute } from './uiSlice.ts';

export const initialReferenceBarUIState: ReferenceBarUIState = {
  referenceViewOpen: false,
  mobileReferenceMenuOpen: false,
  properties: [],
  selectedDataSheet: null,
  alertsPanelAutoOpenedForNavBlock: false,
  focusedAlertType: null,
  hoveredAlertType: null,
  hoveredPropertyGridCellKey: null,
  hoveredOrderDetailsFieldKey: null,
  hoveredCostApproachPropertyId: null,
  __doNotUseDirectly__ActiveID: formatReferenceViewID({ type: 'subject' }),
};

const referenceBarUISlice = createSlice({
  name: 'referenceBarUI',
  initialState: initialReferenceBarUIState,
  reducers: {
    setReferenceViewOpen(state, action: PayloadAction<boolean>) {
      if (!action.payload) {
        state.selectedDataSheet = null;
        state.focusedAlertType = null;
        state.hoveredAlertType = null;
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
        state.alertsPanelAutoOpenedForNavBlock = false;
      }

      state.referenceViewOpen = action.payload;
    },
    clearAlertsPanelAutoOpenedForNavBlock(state) {
      state.alertsPanelAutoOpenedForNavBlock = false;
    },
    setMobileReferenceMenuOpen(state, action: PayloadAction<boolean>) {
      state.mobileReferenceMenuOpen = action.payload;
    },
    setProperties(state, action: PayloadAction<Tkv2ReferenceBarProperty[]>) {
      state.properties = action.payload;
    },
    setSelectedDataSheet(state, action: PayloadAction<DataSheet | null>) {
      if (action.payload !== 'craftsman-alerts') {
        state.focusedAlertType = null;
        state.hoveredAlertType = null;
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
      state.selectedDataSheet = action.payload;
    },
    setFocusedAlertType(state, action: PayloadAction<AlertType | null>) {
      state.focusedAlertType = action.payload;
    },
    setHoveredAlertType(state, action: PayloadAction<AlertType | null>) {
      state.hoveredAlertType = action.payload;
      if (!action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredPropertyGridCellKey(state, action: PayloadAction<PropertyGridCellKey | null>) {
      state.hoveredPropertyGridCellKey = action.payload;
      if (action.payload) {
        state.hoveredOrderDetailsFieldKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredOrderDetailsFieldKey(state, action: PayloadAction<string | null>) {
      state.hoveredOrderDetailsFieldKey = action.payload;
      if (action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredCostApproachPropertyId = null;
      }
    },
    setHoveredCostApproachPropertyId(state, action: PayloadAction<string | null>) {
      state.hoveredCostApproachPropertyId = action.payload;
      if (action.payload) {
        state.hoveredPropertyGridCellKey = null;
        state.hoveredOrderDetailsFieldKey = null;
      }
    },
    setActiveID(state, action: PayloadAction<ReferenceViewID>) {
      state.__doNotUseDirectly__ActiveID = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearOrder, () => initialReferenceBarUIState);
    builder.addCase(processAlertsForRoute, (state, action) => {
      if (action.payload.openAlertsPanel !== false) {
        state.alertsPanelAutoOpenedForNavBlock = true;
      }
    });
  },
});

export const {
  setReferenceViewOpen,
  setMobileReferenceMenuOpen,
  setProperties,
  setSelectedDataSheet,
  clearAlertsPanelAutoOpenedForNavBlock,
  setFocusedAlertType,
  setHoveredAlertType,
  setHoveredPropertyGridCellKey,
  setHoveredOrderDetailsFieldKey,
  setHoveredCostApproachPropertyId,
  setActiveID,
} = referenceBarUISlice.actions;

export const referenceBarUIReducer = referenceBarUISlice.reducer;
